import { useMemo, type ReactNode } from 'react'
import { useForm, usePage } from '@inertiajs/react'
import { SystemScheduleForm, CustomDaysOffSection } from '../Schedules/schedule-sections'
import type { HolidayYearGroup } from '../Schedules/schedule-sections'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { t } from '@/shared/lib/i18n'
import { adminSettingsPath, adminSubscriptionPath } from '@/shared/lib/routes'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type { SharedProps } from '@/shared/types/SharedProps'
import type WebAdminTenantSettings from '@/shared/types/web/WebAdminTenantSettings'
import type { WebAdminCustomDayScheduleRow } from '@/shared/types/web'

interface AdminSettingsPageProps {
  settings: WebAdminTenantSettings
  systemCustomDaySchedule: WebAdminCustomDayScheduleRow | null
  holidaysByYear: HolidayYearGroup[]
  canMutateSchedules: boolean
}

type PageProps = SharedProps & AdminSettingsPageProps

const timeFormatValue = (value: string) => parseInt(value, 10)

const ReadRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div>
    <p className="text-sm font-medium">{label}</p>
    <div className="text-sm text-foreground mt-0.5">{value}</div>
  </div>
)

const AdminSettingsIndex = () => {
  const page = usePage<PageProps>()
  const { settings, systemCustomDaySchedule, holidaysByYear, canMutateSchedules } = page.props
  const { url: pageUrl } = page
  const canEdit = useCan(Resources.SYSTEM_SETTINGS, Actions.UPDATE)

  useBreadcrumbs([
    { label: t('nav.administration') },
    { label: t('nav.subscription'), href: adminSubscriptionPath() },
    { label: t('nav.systemSettings') },
  ])

  const fromSubscription = useMemo(() => {
    const q = pageUrl.includes('?') ? pageUrl.split('?')[1] : ''
    return new URLSearchParams(q).get('from') === 'subscription'
  }, [pageUrl])

  const form = useForm<{
    timeFormat: number
    enableLotAccess: boolean
    enableLogging: boolean
    logAuthenticationCode: 'PR' | 'OL'
  }>({
    timeFormat: settings.timeFormat,
    enableLotAccess: settings.enableLotAccess,
    enableLogging: settings.enableLogging,
    logAuthenticationCode: settings.logAuthenticationCode === 'OL' ? 'OL' : 'PR',
  })

  const errorText = useMemo(() => {
    if (!form.errors) {
      return null
    }
    return Object.values(form.errors)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((value) => typeof value === 'string' && value.length > 0)
      .join(' ')
  }, [form.errors])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!canEdit) {
      return
    }

    form.transform((data) => ({
      settings: {
        timeFormat: data.timeFormat,
        tenant_config_values: {
          enable_lot_access: data.enableLotAccess,
          enable_logging: data.enableLogging,
          // Legacy parity: when logging is off the effective mode is PR, so persist PR
          // regardless of the (hidden) dropdown selection (REBUILD-106).
          log_authentication_code: data.enableLogging ? data.logAuthenticationCode : 'PR',
        },
        ...(fromSubscription ? { from: 'subscription' } : {}),
      },
    }))
    form.patch(adminSettingsPath())
  }

  const generalSection = (
    <section className="space-y-4 rounded-lg border border-border bg-white p-6">
      <h2 className="text-lg font-semibold">{t('admin.settings.sectionGeneral')}</h2>
      <p className="text-muted-foreground max-w-2xl text-sm">
        {t('admin.settings.generalNameCodeReadOnly')}
      </p>
      <div className="text-foreground max-w-xl space-y-3">
        <ReadRow label={t('admin.settings.tenantNameLabel')} value={String(settings.name)} />
        <ReadRow label={t('admin.settings.tenantCodeLabel')} value={String(settings.code)} />
      </div>
    </section>
  )

  const operationsSectionEditable = (
    <section className="space-y-4 rounded-lg border border-border bg-white p-6">
      <h2 className="text-lg font-semibold">{t('admin.settings.sectionOperations')}</h2>
      <p className="text-muted-foreground max-w-2xl text-sm">
        {t('admin.settings.operationsIntro')}
      </p>
      <p className="text-muted-foreground max-w-2xl border-l-2 border-muted pl-3 text-sm">
        <span>{t('admin.settings.operationsTimeFormatReminderLine')}</span>{' '}
        <strong>
          {settings.timeFormat === 12
            ? t('admin.settings.timeFormat12')
            : t('admin.settings.timeFormat24')}
        </strong>
        {'. '}
        {t('admin.settings.operationsTimeFormatReminderHint')}
      </p>
      <div className="max-w-xl space-y-6">
        <div className="flex items-start justify-between gap-4 rounded-md border border-border p-4">
          <div className="space-y-1">
            <Label htmlFor="enableLotAccess">{t('admin.settings.enableLotAccessLabel')}</Label>
            <p className="text-muted-foreground text-sm">
              {t('admin.settings.enableLotAccessHelp')}
            </p>
          </div>
          <Switch
            id="enableLotAccess"
            checked={form.data.enableLotAccess}
            onCheckedChange={(checked) => form.setData('enableLotAccess', checked)}
          />
        </div>
        <div className="rounded-md border border-border p-4 space-y-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('admin.settings.instrumentSecurityLabel')}
          </p>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="enableLogging">{t('admin.settings.enableLoggingLabel')}</Label>
              <p className="text-muted-foreground text-sm">
                {t('admin.settings.enableLoggingHelp')}
              </p>
            </div>
            <Switch
              id="enableLogging"
              checked={form.data.enableLogging}
              onCheckedChange={(checked) => form.setData('enableLogging', checked)}
            />
          </div>
          {form.data.enableLogging && (
            <div className="space-y-1.5 border-t border-border pt-4">
              <Label id="logAuthenticationCodeLabel">
                {t('admin.settings.instrumentSecurityDefaultLabel')}
              </Label>
              <p className="text-muted-foreground text-sm">
                {t('admin.settings.instrumentSecurityDefaultHelp')}
              </p>
              <Select
                value={form.data.logAuthenticationCode}
                onValueChange={(value) =>
                  form.setData('logAuthenticationCode', value === 'OL' ? 'OL' : 'PR')
                }
              >
                <SelectTrigger aria-labelledby="logAuthenticationCodeLabel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PR">{t('admin.settings.loggingModePrLabel')}</SelectItem>
                  <SelectItem value="OL">{t('admin.settings.loggingModeOlLabel')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
      <hr className="border-border" />
      <SystemScheduleForm initialRow={systemCustomDaySchedule} canMutate={canMutateSchedules} />
      <hr className="border-border" />
      <CustomDaysOffSection holidaysByYear={holidaysByYear} canMutate={canMutateSchedules} />
    </section>
  )

  const operationsSectionReadOnly = (
    <section className="space-y-4 rounded-lg border border-border bg-white p-6">
      <h2 className="text-lg font-semibold">{t('admin.settings.sectionOperations')}</h2>
      <div className="text-foreground max-w-xl space-y-3">
        <ReadRow
          label={t('admin.settings.enableLotAccessLabel')}
          value={
            settings.enableLotAccess
              ? t('admin.settings.booleanYes')
              : t('admin.settings.booleanNo')
          }
        />
        <ReadRow
          label={t('admin.settings.instrumentSecurityLabel')}
          value={
            !settings.enableLogging
              ? t('admin.settings.loggingModeOffLabel')
              : settings.logAuthenticationCode === 'OL'
                ? t('admin.settings.loggingModeOlLabel')
                : t('admin.settings.loggingModePrLabel')
          }
        />
      </div>
      <hr className="border-border" />
      <SystemScheduleForm initialRow={systemCustomDaySchedule} canMutate={canMutateSchedules} />
      <hr className="border-border" />
      <CustomDaysOffSection holidaysByYear={holidaysByYear} canMutate={canMutateSchedules} />
    </section>
  )

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">{t('admin.settings.pageTitle')}</h1>
        <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
          {canEdit
            ? t('admin.settings.pageDescription')
            : t('admin.settings.pageDescriptionViewOnly')}
        </p>
        {!canEdit && (
          <p className="text-amber-800 mt-2 text-sm">{t('admin.settings.readOnlyNote')}</p>
        )}
      </div>

      {canEdit ? (
        <form onSubmit={handleSubmit} className="space-y-8" noValidate>
          {generalSection}
          <section className="space-y-4 rounded-lg border border-border bg-white p-6">
            <h2 className="text-lg font-semibold">{t('admin.settings.sectionSecurity')}</h2>
            <div className="grid max-w-xl gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="sessionTimeout">{t('admin.settings.sessionTimeoutLabel')}</Label>
                <Input
                  id="sessionTimeout"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="tabular-nums bg-muted"
                  value={String(settings.sessionTimeout)}
                  readOnly
                  disabled
                />
                <p className="text-muted-foreground text-sm">
                  {t('admin.settings.sessionTimeoutManagedNote')}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label id="timeFormatLabel">{t('admin.settings.timeFormatLabel')}</Label>
                <Select
                  value={String(form.data.timeFormat)}
                  onValueChange={(value) => {
                    form.setData('timeFormat', timeFormatValue(value))
                  }}
                >
                  <SelectTrigger aria-labelledby="timeFormatLabel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12">{t('admin.settings.timeFormat12')}</SelectItem>
                    <SelectItem value="24">{t('admin.settings.timeFormat24')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>
          {operationsSectionEditable}
          {errorText && (
            <div
              className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive"
              role="alert"
            >
              {errorText}
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={form.processing}>
              {t('admin.settings.saveButton')}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-8">
          {generalSection}
          <section className="space-y-4 rounded-lg border border-border bg-white p-6">
            <h2 className="text-lg font-semibold">{t('admin.settings.sectionSecurity')}</h2>
            <div className="text-foreground max-w-xl space-y-3">
              <ReadRow
                label={t('admin.settings.sessionTimeoutLabel')}
                value={String(settings.sessionTimeout)}
              />
              <ReadRow
                label={t('admin.settings.timeFormatLabel')}
                value={
                  settings.timeFormat === 12
                    ? t('admin.settings.timeFormat12')
                    : t('admin.settings.timeFormat24')
                }
              />
            </div>
          </section>
          {operationsSectionReadOnly}
        </div>
      )}
    </div>
  )
}

export default AdminSettingsIndex
